import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  User,
  Sparkles,
  Save,
  RotateCcw
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useUniversalTextSizing, getUniversalTextConfig } from '../utils/cardTitleSizing';
import { CardTitle, BodyText, SmallText, CaptionText, UniversalButton, useUniversalSizing } from './UniversalText';

// Reuse types from StyleSpecificScoringCard
export interface StyleScoreSheet {
  styleId: string;
  styleName: string;
  styleSpecificData?: Record<string, Record<string, string[]>>;
  categories?: Record<string, string[]>;
}

export interface FeedbackData {
  [key: string]: 'good' | 'needs-work';
}

export interface JudgeStyleFeedback {
  good?: Array<{category: string, criteria: string}>;
  'needs-work'?: Array<{category: string, criteria: string}>;
}

export interface StyleFeedbackEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentRoutine?: any | null;
  judgeIds: string[];
  judgeProfiles: Record<string, any>;
  judgeStyleFeedback: Record<string, JudgeStyleFeedback>; // judgeId -> styleFeedback
  judgePositions?: Record<string, number>; // judgeId -> position number
  styleScoreSheets?: StyleScoreSheet[];
  onFeedbackUpdate: (judgeId: string, category: string, criteria: string, feedbackType: 'good' | 'needs-work' | null, batchedData?: JudgeStyleFeedback) => void;
  className?: string;
  initialJudgeIndex?: number; // New prop to open modal to specific judge
}

// Helper function to convert database format to internal format
const convertDatabaseFeedbackToInternal = (databaseFeedback: any): FeedbackData => {
  if (!databaseFeedback || typeof databaseFeedback !== 'object') {
    return {};
  }

  const internalFeedback: FeedbackData = {};

  // Check if this is already in individual format (old format: key-value pairs where values are 'good' or 'needs-work')
  const isIndividualFormat = Object.values(databaseFeedback).some(value => 
    value === 'good' || value === 'needs-work'
  );

  if (isIndividualFormat) {
    // Already in the correct format, just return it
    // Already in individual format - logging removed to reduce console spam
    return databaseFeedback as FeedbackData;
  }

  // Handle the DynamoDB format
  if (databaseFeedback.good && databaseFeedback.good.L && Array.isArray(databaseFeedback.good.L)) {
    databaseFeedback.good.L.forEach((item: any) => {
      if (item && item.M && item.M.category && item.M.category.S && item.M.criteria && item.M.criteria.S) {
        const key = `${item.M.category.S}:${item.M.criteria.S}`;
        internalFeedback[key] = 'good';
      }
    });
  }

  if (databaseFeedback['needs-work'] && databaseFeedback['needs-work'].L && Array.isArray(databaseFeedback['needs-work'].L)) {
    databaseFeedback['needs-work'].L.forEach((item: any) => {
      if (item && item.M && item.M.category && item.M.category.S && item.M.criteria && item.M.criteria.S) {
        const key = `${item.M.category.S}:${item.M.criteria.S}`;
        internalFeedback[key] = 'needs-work';
      }
    });
  }

  // Handle the simplified grouped format (for backward compatibility)
  if (databaseFeedback.good && Array.isArray(databaseFeedback.good)) {
    databaseFeedback.good.forEach((item: any) => {
      if (item && typeof item === 'object' && item.category && item.criteria) {
        const key = `${item.category}:${item.criteria}`;
        internalFeedback[key] = 'good';
      }
    });
  }

  if (databaseFeedback['needs-work'] && Array.isArray(databaseFeedback['needs-work'])) {
    databaseFeedback['needs-work'].forEach((item: any) => {
      if (item && typeof item === 'object' && item.category && item.criteria) {
        const key = `${item.category}:${item.criteria}`;
        internalFeedback[key] = 'needs-work';
      }
    });
  }

  // Conversion logging removed to reduce console spam

  return internalFeedback;
};

// Helper function to flatten the style score sheet (reused from StyleSpecificScoringCard)
const flattenStyleScoreSheet = (styleSheet: StyleScoreSheet): { categories: Record<string, string[]>, groupInfo: Record<string, { groupName: string, groupIndex: number }> } => {
  // If using the old format, return as-is with no group info
  if (styleSheet.categories && !styleSheet.styleSpecificData) {
    return { 
      categories: styleSheet.categories, 
      groupInfo: {} 
    };
  }

  // If using the new format, flatten it with group information
  if (styleSheet.styleSpecificData) {
    const flattened: Record<string, string[]> = {};
    const groupInfo: Record<string, { groupName: string, groupIndex: number }> = {};
    
    // REVERSE the order of GLOBAL category groups (e.g., Technical before Performance)
    const reversedGlobalCategories = Object.entries(styleSheet.styleSpecificData).reverse();
    
    let groupIndex = 0;
    reversedGlobalCategories.forEach(([globalCategoryName, genreSpecificCategories]) => {
      // Check if we have order metadata for this global category
      const orderMetadata = (styleSheet as any).orderMetadata?.[globalCategoryName];
      let categoriesEntries;
      
      if (orderMetadata && Array.isArray(orderMetadata)) {
        // Use the stored order from metadata (user's custom drag-and-drop order)
        categoriesEntries = orderMetadata.map(categoryName => [categoryName, genreSpecificCategories[categoryName]]).filter(([name, criteria]) => criteria !== undefined);
        // Add any categories not in the order metadata (newly added categories)
        Object.entries(genreSpecificCategories).forEach(([categoryName, criteria]) => {
          if (!orderMetadata.includes(categoryName)) {
            categoriesEntries.push([categoryName, criteria]);
          }
        });
      } else {
        // No order metadata, use the stored order as-is (no automatic reversing for genre-specific categories)
        categoriesEntries = Object.entries(genreSpecificCategories);
      }
      
      categoriesEntries.forEach(([genreSpecificCategoryName, criteria]) => {
        // Use just the genre-specific category name as the key (no parent group text)
        flattened[genreSpecificCategoryName] = criteria;
        // Store group information for visual styling (using global category for grouping)
        groupInfo[genreSpecificCategoryName] = {
          groupName: globalCategoryName,
          groupIndex: groupIndex
        };
      });
      groupIndex++;
    });
    
    return { categories: flattened, groupInfo };
  }

  // Fallback to empty object
  return { categories: {}, groupInfo: {} };
};

// Helper function to get group-specific styling (reused from StyleSpecificScoringCard)
const getGroupStyling = (groupIndex: number) => {
  const styles = [
    // Group 0: Purple/Violet theme
    {
      headerBg: 'bg-purple-500/20',
      headerBorder: 'border-purple-400/40',
      headerText: 'text-purple-200',
      contentBg: 'bg-purple-500/5'
    },
    // Group 1: Blue theme  
    {
      headerBg: 'bg-blue-500/20',
      headerBorder: 'border-blue-400/40', 
      headerText: 'text-blue-200',
      contentBg: 'bg-blue-500/5'
    },
    // Group 2: Green theme
    {
      headerBg: 'bg-green-500/20',
      headerBorder: 'border-green-400/40',
      headerText: 'text-green-200', 
      contentBg: 'bg-green-500/5'
    },
    // Group 3: Orange theme
    {
      headerBg: 'bg-orange-500/20',
      headerBorder: 'border-orange-400/40',
      headerText: 'text-orange-200',
      contentBg: 'bg-orange-500/5'
    },
    // Group 4: Pink theme
    {
      headerBg: 'bg-pink-500/20',
      headerBorder: 'border-pink-400/40',
      headerText: 'text-pink-200',
      contentBg: 'bg-pink-500/5'
    }
  ];
  
  // Cycle through styles if we have more groups than styles
  return styles[groupIndex % styles.length];
};

// Memoized criteria item component for better performance
const CriteriaItem = memo<{
  categoryName: string;
  index: number;
  criteriaText: string;
  feedback?: 'good' | 'needs-work';
  isAnimating: boolean;
  isUpdating: boolean;
  onFeedbackChange: (categoryName: string, index: number, criteriaText: string, feedbackType: 'good' | 'needs-work') => void;
  universalConfig: any;
}>(({ 
  categoryName, 
  index, 
  criteriaText, 
  feedback, 
  isAnimating,
  isUpdating,
  onFeedbackChange, 
  universalConfig 
}) => {
  const handleNeedsWorkClick = useCallback(() => {
    onFeedbackChange(categoryName, index, criteriaText, 'needs-work');
  }, [categoryName, index, criteriaText, onFeedbackChange]);

  const handleGoodClick = useCallback(() => {
    onFeedbackChange(categoryName, index, criteriaText, 'good');
  }, [categoryName, index, criteriaText, onFeedbackChange]);

  return (
    <motion.div
      className={`relative p-2 rounded-lg border transition-all duration-200 cursor-pointer overflow-hidden h-9 flex items-center ${
        feedback === 'good' 
          ? 'bg-green-500/30 border-green-400/50 shadow-lg shadow-green-500/30' 
          : feedback === 'needs-work'
          ? 'bg-orange-500/30 border-orange-400/50 shadow-lg shadow-orange-500/30'
          : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
      }`}
      animate={isAnimating ? { scale: [1, 1.02, 1] } : {}}
      transition={{ duration: 0.3 }}
    >
      {/* Left Half - Needs Work */}
      <div 
        className="absolute left-0 top-0 w-1/2 h-full z-10 hover:bg-orange-500/10 transition-all duration-200"
        onClick={handleNeedsWorkClick}
        title="Click to mark as 'Needs Work'"
      />
      
      {/* Right Half - Good */}
      <div 
        className="absolute right-0 top-0 w-1/2 h-full z-10 hover:bg-green-500/10 transition-all duration-200"
        onClick={handleGoodClick}
        title="Click to mark as 'Good'"
      />
      
      {/* Content */}
      <div className="relative z-0 w-full min-w-0">
        {isUpdating ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex space-x-1">
              <div className="w-1 h-1 bg-white/60 rounded-full animate-pulse"></div>
              <div className="w-1 h-1 bg-white/60 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-1 h-1 bg-white/60 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        ) : (
          <div className={`truncate ${
            feedback === 'good' 
              ? 'text-green-100 font-medium' 
              : feedback === 'needs-work'
              ? 'text-orange-100 font-medium'
              : 'text-white/90'
          } ${universalConfig.captionText}`} title={criteriaText}>
            {criteriaText}
          </div>
        )}
      </div>
    </motion.div>
  );
});

CriteriaItem.displayName = 'CriteriaItem';

const StyleFeedbackEditorModal: React.FC<StyleFeedbackEditorModalProps> = ({
  isOpen,
  onClose,
  currentRoutine = null,
  judgeIds,
  judgeProfiles,
  judgeStyleFeedback,
  judgePositions = {},
  styleScoreSheets = [],
  onFeedbackUpdate,
  className = "",
  initialJudgeIndex = 0
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  // Use universal text sizing for consistency
  const { textSize } = useUniversalTextSizing();
  const universalConfig = getUniversalTextConfig(textSize);
  
  // Modal state
  const [currentJudgeIndex, setCurrentJudgeIndex] = useState(initialJudgeIndex);
  const [feedback, setFeedback] = useState<FeedbackData>({});
  const [animatingItems, setAnimatingItems] = useState<Set<string>>(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingFeedback, setIsUpdatingFeedback] = useState(false);
  
  // Reset judge index when modal opens with new initialJudgeIndex OR when initialJudgeIndex changes
  useEffect(() => {
    if (initialJudgeIndex >= 0 && initialJudgeIndex < judgeIds.length) {
      setCurrentJudgeIndex(initialJudgeIndex);
      // Only clear unsaved changes when modal first opens, not when navigating
      if (isOpen) {
        // Check if we're actually changing judges
        if (currentJudgeIndex !== initialJudgeIndex) {
          setHasUnsavedChanges(false);
        }
      }
    }
  }, [isOpen, initialJudgeIndex, judgeIds.length]);
  
  // Get current judge
  const currentJudgeId = judgeIds[currentJudgeIndex] || '';
  const currentJudgeProfile = judgeProfiles[currentJudgeId] || currentJudgeId;
  
  // Get the appropriate style score sheet for the current routine
  const currentStyleSheet = useMemo((): StyleScoreSheet | null => {
    if (!currentRoutine || !styleScoreSheets || styleScoreSheets.length === 0) {
      return null;
    }
    
    // Try to match by style category
    const routineStyle = currentRoutine.category ||
                        currentRoutine.style || 
                        currentRoutine.styleCategory || 
                        currentRoutine['Dance Style'] ||
                        currentRoutine['Style'] ||
                        currentRoutine.division ||
                        currentRoutine.Category ||
                        currentRoutine.Type ||
                        currentRoutine.type;
    
    if (routineStyle) {
      // Find matching style score sheet - prioritize exact matches first
      const styleKey = routineStyle.toLowerCase().trim();
      
      // First try to find an exact match
      let matchingSheet = styleScoreSheets.find(sheet => {
        if (!sheet.styleId) return false;
        const sheetStyleKey = sheet.styleId.toLowerCase().trim();
        return sheetStyleKey === styleKey;
      });
      
      // If no exact match, try partial matches but prioritize longer matches
      if (!matchingSheet) {
        // Sort by styleId length (descending) to prioritize longer, more specific matches
        const sortedSheets = [...styleScoreSheets].sort((a, b) => 
          (b.styleId?.length || 0) - (a.styleId?.length || 0)
        );
        
        matchingSheet = sortedSheets.find(sheet => {
          if (!sheet.styleId) return false;
          const sheetStyleKey = sheet.styleId.toLowerCase().trim();
          
          // Check for partial matches
          return styleKey.includes(sheetStyleKey) || 
                 sheetStyleKey.includes(styleKey);
        });
      }
      
      if (matchingSheet) {
        return matchingSheet;
      }
    }
    
    return null;
  }, [currentRoutine, styleScoreSheets]);

  // Get the flattened categories and group info for the current style sheet
  const { flattenedCategories, groupInfo } = useMemo(() => {
    if (!currentStyleSheet) return { flattenedCategories: {}, groupInfo: {} };
    const result = flattenStyleScoreSheet(currentStyleSheet);
    return { flattenedCategories: result.categories, groupInfo: result.groupInfo };
  }, [currentStyleSheet]);

  // Load feedback for current judge - but don't override unsaved changes
  useEffect(() => {
    // Don't reload if we have unsaved changes or are currently saving
    if (hasUnsavedChanges || isSaving) {
      console.log('Skipping feedback reload due to unsaved changes or saving state');
      return;
    }

      if (currentJudgeId && judgeStyleFeedback[currentJudgeId]) {
        const convertedFeedback = convertDatabaseFeedbackToInternal(judgeStyleFeedback[currentJudgeId]);
        
        // Simple check: only update if we don't already have the same data
        setFeedback(prev => {
          if (JSON.stringify(prev) === JSON.stringify(convertedFeedback)) {
            return prev; // No change, skip update
          }
          // Loading feedback logging removed to reduce console spam
          return convertedFeedback;
        });
      } else {
        setFeedback({});
      }
    setIsSaving(false); // Reset saving state when switching judges
  }, [currentJudgeId, judgeStyleFeedback, hasUnsavedChanges, isSaving]);

  // Navigation handlers
  const goToPreviousJudge = useCallback(() => {
    setCurrentJudgeIndex(prev => (prev - 1 + judgeIds.length) % judgeIds.length);
    // Clear unsaved changes when navigating to allow loading new judge's feedback
    setHasUnsavedChanges(false);
  }, [judgeIds.length]);

  const goToNextJudge = useCallback(() => {
    setCurrentJudgeIndex(prev => (prev + 1) % judgeIds.length);
    // Clear unsaved changes when navigating to allow loading new judge's feedback
    setHasUnsavedChanges(false);
  }, [judgeIds.length]);

  // Animation cleanup
  const animationTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  
  const clearAnimationTimeout = useCallback((key: string) => {
    const timeoutId = animationTimeoutRef.current.get(key);
    if (timeoutId) {
      clearTimeout(timeoutId);
      animationTimeoutRef.current.delete(key);
    }
  }, []);

  // Handle feedback for a specific criteria
  const handleCriteriaFeedback = useCallback((categoryName: string, criteriaIndex: number, criteriaText: string, feedbackType: 'good' | 'needs-work') => {
    const key = `${categoryName}:${criteriaText}`;
    
    // Clear any existing animation timeout for this key
    clearAnimationTimeout(key);
    
    // Add animation
    setAnimatingItems(prev => {
      if (prev.has(key)) return prev;
      return new Set(prev).add(key);
    });
    
    // Set timeout for animation cleanup
    const timeoutId = setTimeout(() => {
      setAnimatingItems(prev => {
        if (!prev.has(key)) return prev;
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
      animationTimeoutRef.current.delete(key);
    }, 300);
    
    animationTimeoutRef.current.set(key, timeoutId);

    // Update local feedback state
    setFeedback(prev => {
      const currentValue = prev[key];
      let newFeedback: FeedbackData;
      
      // Toggle feedback: if same type is clicked, remove it; otherwise set new type
      if (currentValue === feedbackType) {
        const { [key]: removed, ...rest } = prev;
        newFeedback = rest;
      } else {
        newFeedback = { ...prev, [key]: feedbackType };
      }
      
      setHasUnsavedChanges(true);
      return newFeedback;
    });
  }, [clearAnimationTimeout]);

  // Save feedback for current judge - batched update
  const saveFeedback = useCallback(async () => {
    if (!currentJudgeId || isSaving) return;

    setIsSaving(true);
    setIsUpdatingFeedback(true);
    try {
      // Convert internal feedback format to simplified format (let DynamoDB handle attribute conversion)
      const goodFeedback: Array<{category: string, criteria: string}> = [];
      const needsWorkFeedback: Array<{category: string, criteria: string}> = [];
      
      Object.entries(feedback).forEach(([key, feedbackType]) => {
        const [category, criteria] = key.split(':', 2);
        if (feedbackType === 'good') {
          goodFeedback.push({ category, criteria });
        } else if (feedbackType === 'needs-work') {
          needsWorkFeedback.push({ category, criteria });
        }
      });

      const styleFeedbackData: {
        good?: Array<{category: string, criteria: string}>;
        'needs-work'?: Array<{category: string, criteria: string}>;
      } = {};

      if (goodFeedback.length > 0) styleFeedbackData.good = goodFeedback;
      if (needsWorkFeedback.length > 0) styleFeedbackData['needs-work'] = needsWorkFeedback;

      console.log('Sending batched style feedback update:', {
        judgeId: currentJudgeId,
        styleFeedbackData: JSON.stringify(styleFeedbackData, null, 2)
      });

      console.log('About to call onFeedbackUpdate with 5 arguments:', {
        arg1: currentJudgeId,
        arg2: '',
        arg3: '',
        arg4: null,
        arg5: styleFeedbackData
      });

      // Send single batched update with all style feedback
      const result = await onFeedbackUpdate(currentJudgeId, '', '', null, styleFeedbackData);
      
      console.log('onFeedbackUpdate result:', result);

      setHasUnsavedChanges(false);
      console.log('Style feedback saved successfully for judge:', currentJudgeId);
      
      // Force a reload of the feedback after a short delay to get the updated data
      setTimeout(() => {
        console.log('Force reloading feedback after successful save');
        if (currentJudgeId && judgeStyleFeedback[currentJudgeId]) {
          const convertedFeedback = convertDatabaseFeedbackToInternal(judgeStyleFeedback[currentJudgeId]);
          setFeedback(convertedFeedback);
        }
        setIsUpdatingFeedback(false); // Clear the updating state after reload
      }, 2000); // Wait 2 seconds for polling to update
    } catch (error) {
      console.error('Failed to save style feedback:', error);
      setIsUpdatingFeedback(false); // Clear updating state on error
    } finally {
      setIsSaving(false);
    }
  }, [currentJudgeId, feedback, onFeedbackUpdate, isSaving, judgeStyleFeedback]);

  // Clear all feedback
  const clearAllFeedback = useCallback(() => {
    setFeedback({});
    setHasUnsavedChanges(true);
  }, []);

  // Reset to original feedback
  const resetFeedback = useCallback(() => {
    if (currentJudgeId && judgeStyleFeedback[currentJudgeId]) {
      const convertedFeedback = convertDatabaseFeedbackToInternal(judgeStyleFeedback[currentJudgeId]);
      setFeedback(convertedFeedback);
    } else {
      setFeedback({});
    }
    setHasUnsavedChanges(false);
  }, [currentJudgeId, judgeStyleFeedback]);

  // Cleanup animation timeouts on unmount
  useEffect(() => {
    return () => {
      animationTimeoutRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      animationTimeoutRef.current.clear();
    };
  }, []);

  // Calculate feedback counts
  const feedbackCounts = useMemo(() => {
    const totalFeedbackCount = Object.keys(feedback).length;
    const goodCount = Object.values(feedback).filter(f => f === 'good').length;
    const needsWorkCount = Object.values(feedback).filter(f => f === 'needs-work').length;
    
    return { totalFeedbackCount, goodCount, needsWorkCount };
  }, [feedback]);

  // Don't render if not open
  if (!isOpen) return null;

  // Early return if no style sheet is available
  if (!currentStyleSheet || Object.keys(flattenedCategories).length === 0) {
    const noDataContent = (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50 text-purple-400" />
              <BodyText className="font-medium text-white mb-2">
                No Style-Specific Criteria Available
              </BodyText>
              <SmallText className="text-white/70 mb-4">
                Configure style score sheets in the admin settings to enable style-specific feedback editing.
              </SmallText>
              <UniversalButton 
                onClick={onClose} 
                variant="primary"
              >
                Close
              </UniversalButton>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );

    return createPortal(noDataContent, document.body);
  }

  const { totalFeedbackCount, goodCount, needsWorkCount } = feedbackCounts;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-2"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl w-[55vw] h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-purple-400" />
              <div>
                <CardTitle className="text-white">
                  {currentStyleSheet?.styleName || 'STYLE'} FEEDBACK EDITOR
                </CardTitle>
                <SmallText className="text-white/70 mt-1">
                  Edit style feedback for judges - {currentRoutine?.name || 'Current Routine'}
                </SmallText>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-white/70 hover:text-white" />
            </button>
          </div>

          {/* Judge Navigation */}
          <div className="flex items-center justify-between p-4 bg-white/5 border-b border-white/10">
            <button
              onClick={goToPreviousJudge}
              disabled={judgeIds.length <= 1}
              className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-white/70" />
            </button>

            <div className="flex items-center gap-3">
              {/* Judge Photo */}
              {typeof currentJudgeProfile === 'object' && currentJudgeProfile.photoUrl ? (
                <img 
                  src={currentJudgeProfile.photoUrl} 
                  alt={currentJudgeProfile.name || currentJudgeId}
                  className="w-10 h-10 rounded-full object-cover border-2 border-white/20"
                />
              ) : (
                <User className="w-10 h-10 text-white/70 p-2 rounded-full bg-white/10" />
              )}
              <div className="text-left">
                <div className="font-medium text-white text-base leading-tight">
                  {typeof currentJudgeProfile === 'object' ? currentJudgeProfile.name || currentJudgeId : currentJudgeId}
                </div>
                <div className="text-white/70 text-sm leading-tight">
                  {judgePositions[currentJudgeId] !== undefined 
                    ? `Judge Position ${judgePositions[currentJudgeId]}`
                    : `Judge ${currentJudgeIndex + 1}`
                  }
                </div>
              </div>
            </div>

            <button
              onClick={goToNextJudge}
              disabled={judgeIds.length <= 1}
              className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-white/70" />
            </button>
          </div>

          {/* Feedback Summary */}
          <div className="flex items-center justify-between p-4 bg-white/5 border-b border-white/10">
            <div className="flex gap-3">
              <span className={`${universalConfig.badgeSize} bg-green-500/20 text-green-300 rounded-full border border-green-400/30`}>
                ✓ {goodCount} Good
              </span>
              <span className={`${universalConfig.badgeSize} bg-orange-500/20 text-orange-300 rounded-full border border-orange-400/30`}>
                ⚠ {needsWorkCount} Needs Work
              </span>
              {hasUnsavedChanges && (
                <span className={`${universalConfig.badgeSize} bg-yellow-500/20 text-yellow-300 rounded-full border border-yellow-400/30`}>
                  ● Unsaved Changes
                </span>
              )}
              {isUpdatingFeedback && (
                <span className={`${universalConfig.badgeSize} bg-blue-500/20 text-blue-300 rounded-full border border-blue-400/30 flex items-center gap-1`}>
                  <div className="flex space-x-1">
                    <div className="w-1 h-1 bg-blue-300 rounded-full animate-pulse"></div>
                    <div className="w-1 h-1 bg-blue-300 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-1 h-1 bg-blue-300 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                  Updating...
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <UniversalButton
                onClick={clearAllFeedback}
                variant="secondary"
                size="small"
                className="flex items-center gap-1"
                disabled={totalFeedbackCount === 0}
              >
                <RotateCcw className="w-3 h-3" />
                Clear All
              </UniversalButton>
              
              {hasUnsavedChanges && (
                <>
                  <UniversalButton
                    onClick={resetFeedback}
                    variant="secondary"
                    size="small"
                  >
                    Reset
                  </UniversalButton>
                  <UniversalButton
                    onClick={saveFeedback}
                    variant="primary"
                    size="small"
                    className="flex items-center gap-1"
                    disabled={isSaving}
                  >
                    <Save className="w-3 h-3" />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </UniversalButton>
                </>
              )}
            </div>
          </div>

          {/* Category Columns Layout - Full Height */}
          <div className="flex-1 min-h-0 overflow-hidden p-4">
            <div className="h-full overflow-y-auto">
              <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-6 2xl:columns-8 gap-4">
                {Object.entries(flattenedCategories).map(([categoryName, criteria]) => {
                  const groupStyling = groupInfo[categoryName] ? getGroupStyling(groupInfo[categoryName].groupIndex) : {
                    headerBg: 'bg-white/10',
                    headerBorder: 'border-white/20',
                    headerText: 'text-white',
                    contentBg: 'bg-white/5'
                  };

                  return criteria.map((criteriaText, index) => (
                    <div key={`${categoryName}-${index}`} className="break-inside-avoid mb-3">
                      {/* Category Header - only show for first item of each category */}
                      {index === 0 && (
                        <div className={`border rounded-lg px-3 py-2 mb-2 ${groupStyling.headerBg} ${groupStyling.headerBorder}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`font-bold truncate ${groupStyling.headerText} ${universalConfig.smallText}`} title={categoryName}>
                              {categoryName}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Individual Criteria Item */}
                      <div className="px-1">
                        <CriteriaItem
                          categoryName={categoryName}
                          index={index}
                          criteriaText={criteriaText}
                          feedback={feedback[`${categoryName}:${criteriaText}`]}
                          isAnimating={animatingItems.has(`${categoryName}:${criteriaText}`)}
                          isUpdating={isUpdatingFeedback}
                          onFeedbackChange={handleCriteriaFeedback}
                          universalConfig={universalConfig}
                        />
                      </div>
                    </div>
                  ));
                }).flat()}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

// Memoize the main component to prevent unnecessary re-renders
const MemoizedStyleFeedbackEditorModal = memo(StyleFeedbackEditorModal);
MemoizedStyleFeedbackEditorModal.displayName = 'StyleFeedbackEditorModal';

export default MemoizedStyleFeedbackEditorModal;
